import GoogleIcon from '@mui/icons-material/Google';
import { Alert, Box, Button, CircularProgress, Stack, Tooltip } from '@mui/material';

import { PasswordField } from '@/components/forms';
import { PasswordRequirements } from '@/features/identity/components/PasswordRequirements';
import { PasswordStrengthIndicator } from '@/features/identity/components/PasswordStrengthIndicator';
import { useChangePassword } from '@/features/identity/hooks/useChangePassword';
import type { OAuthProvider } from '@/types/auth.types';

export interface ChangePasswordFormProps {
  /**
   * `CurrentUser.hasPassword`. Required rather than defaulted, because either default is wrong for a
   * real class of account: true asks a Google-created account for a password it does not have, false
   * hides the current-password field from an account that must confirm one.
   */
  hasPassword: boolean;
  /**
   * `CurrentUser.linkedOAuthProviders`. Consulted only when the account has no password, to decide
   * whether there is a provider this client can re-authenticate with.
   */
  linkedProviders: OAuthProvider[];
}

/**
 * The Change Password screen's form (`/account/change-password`), in whichever of its two modes the
 * account is in.
 *
 * <p>An account with a WillnQ password confirms it before replacing it. An account created through an
 * external provider has none (BR-8) and is setting its first, so there is no current-password field and
 * a fresh Google sign-in takes its place. Both apply the same complexity rules and leave the provider
 * link untouched.</p>
 *
 * <p>The Google step is a credential, not a formality: confirming the current password is what stops a
 * stolen session from replacing a password, and without an equivalent here a stolen session could mint
 * one. The user never types their Google password; Google asks, in its own window.</p>
 */
export function ChangePasswordForm({ hasPassword, linkedProviders }: ChangePasswordFormProps) {
  const { form, onSubmit, serverError, googleConfigured } = useChangePassword(hasPassword);
  const { control, formState } = form;
  const newPassword = form.watch('newPassword');

  // Google is the only provider this client can drive (see SocialLoginButtons), so a passwordless
  // account without it has no route here and is told so rather than shown a button the server would
  // refuse anyway.
  const canUseGoogle = linkedProviders.includes('Google');
  const blocked = !hasPassword && (!canUseGoogle || !googleConfigured);

  return (
    <Stack component="form" onSubmit={onSubmit} spacing={2} noValidate sx={{ maxWidth: 420 }}>
      {serverError ? <Alert severity="error">{serverError}</Alert> : null}

      {hasPassword ? (
        <PasswordField
          name="currentPassword"
          control={control}
          label="Current password"
          autoComplete="current-password"
        />
      ) : canUseGoogle ? (
        // Answers "will I lose my Google sign-in?" up front. Leaving that unanswered is what stops
        // people setting a password at all.
        <Alert severity="info">
          This account signs in with Google and has no WillnQ password yet. Choose one below, and
          we&apos;ll ask you to verify with Google first to confirm it&apos;s you. You&apos;ll still be
          able to continue with Google exactly as you do now.
        </Alert>
      ) : (
        <Alert severity="info">
          We can&apos;t confirm your identity automatically for this account, so a WillnQ password
          can&apos;t be set here. Contact support.
        </Alert>
      )}

      <PasswordField
        name="newPassword"
        control={control}
        label="New password"
        autoComplete="new-password"
        disabled={blocked}
      />
      <PasswordStrengthIndicator password={newPassword} />
      <PasswordRequirements password={newPassword} />
      <PasswordField
        name="confirmNewPassword"
        control={control}
        label="Confirm new password"
        autoComplete="new-password"
        disabled={blocked}
      />

      <Tooltip
        title={
          !hasPassword && canUseGoogle && !googleConfigured
            ? 'Google sign-in is not configured in this environment'
            : ''
        }
      >
        {/* A disabled Button fires no events, so the tooltip needs an enabled wrapper to hang on. */}
        <Box component="span" sx={{ alignSelf: 'flex-start' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={formState.isSubmitting || blocked}
            startIcon={
              formState.isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : hasPassword ? undefined : (
                <GoogleIcon />
              )
            }
          >
            {hasPassword ? 'Change password' : 'Verify with Google and set password'}
          </Button>
        </Box>
      </Tooltip>
    </Stack>
  );
}
